import {} from "@/analytics/event-logger";
import { Button } from "@/components/base/button";
import { Heading } from "@/components/base/heading";
import { InputErrorMessage } from "@/components/base/input/input-error-message";
import { FormStateContext } from "@/components/bli-medlem/form-state-context";
import { TermsCheckbox } from "@/components/form-input/terms-checkbox";
import { HorizontalContainer } from "@/components/layout/horizontal-container";
import { HorizontalSpacer } from "@/components/layout/horizontal-spacer";
import { PageContent } from "@/components/layout/page-content";
import { VerticalContainer } from "@/components/layout/vertical-container";
import { Price } from "@/components/ui/price";
import { Text } from "@/components/ui/text";
import type {
  ArticleItem,
  OrderFragment,
  PaymentPeriod,
  SubscriptionItem,
  ValueCardItem,
} from "@/generated/client.generated";
import { KundeserviceRoutes, paths } from "@/utils/paths-utils";
import { capitalizeWord } from "@/utils/string-utils";
import dayjs from "dayjs";
import type Joi from "joi";
import { type SyntheticEvent, useContext } from "react";
import { InitialPaymentExplanation } from "./initial-payment-explanation";
import { SummaryItem } from "./summary-item";

require("dayjs/locale/nb");
dayjs.locale("nb");

interface SummaryProps {
  subscription?: SubscriptionItem;
  order?: OrderFragment;
  loading: boolean;
  handleNext: () => Promise<void>;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  validation?: Joi.ValidationResult<any>;
  serverSideError?: string;
}

export function Summary({ subscription, order, loading, handleNext, validation, serverSideError }: SummaryProps) {
  const { formData, goTo, setFormData } = useContext(FormStateContext);

  const amount = subscription?.recurringPayment?.price.amount;

  const articles = order?.items.filter((item: { __typename: string }) => item.__typename === "ArticleItem") as
    | Array<ArticleItem>
    | undefined;

  const valuecards = order?.items.filter((item: { __typename: string }) => item.__typename === "ValueCardItem") as
    | Array<ValueCardItem>
    | undefined;

  return (
    <PageContent width="x-small">
      <VerticalContainer horizontalPlacement="left" width="full">
        <HorizontalContainer horizontalPlacement="center" width="full">
          <Heading type="h2">Du har valgt</Heading>
        </HorizontalContainer>
        <HorizontalSpacer />

        <hr />
        <HorizontalSpacer />

        <SummaryItem heading="Aldersgruppe:" horizontalPlacement="space" onClick={() => goTo(0)} showRuler={true}>
          <Text align="right" value={formData.customerType?.title ?? ""} weight="bold" />
        </SummaryItem>

        <SummaryItem heading="Primærsenter:" horizontalPlacement="space" onClick={() => goTo(1)} showRuler={true}>
          <Text align="right" value={formData.mainCenter?.page.title ?? ""} weight="bold" />
        </SummaryItem>

        {formData.organization && (
          <SummaryItem heading="Bedrift:" horizontalPlacement="space" onClick={() => goTo(1)} showRuler={true}>
            <Text align="right" value={formData.organization.name ?? ""} weight="bold" />
          </SummaryItem>
        )}
        <SummaryItem heading="Medlemskap:" horizontalPlacement="space" onClick={() => goTo(2)} showRuler={true}>
          <Text align="right" value={formData.accountType?.name ?? ""} weight="bold" />
        </SummaryItem>

        {subscription?.recurringPayment && (
          <SummaryItem heading="Pris per måned:" horizontalPlacement="space" showRuler={true} hasPlaceholderIcon={true}>
            <Price amount={amount || 0} weight="bold" />
          </SummaryItem>
        )}

        <HorizontalSpacer size="medium" />

        <HorizontalContainer horizontalPlacement="center" verticalPlacement="top" width="full">
          <Heading type="h2">Til betaling i dag</Heading>
        </HorizontalContainer>
        <HorizontalSpacer />

        {subscription?.initialPaymentForSubscription ? (
          <>
            <SummaryItem
              heading={`Denne måneden (${dayjs(
                subscription.initialPaymentForSubscription.initialPaymentPeriod.period.start,
              ).format("DD.MM.")} - ${dayjs(
                subscription.initialPaymentForSubscription.initialPaymentPeriod.period.end,
              ).format("DD.MM.")})`}
              key={`${subscription.id}current-month`}
              horizontalPlacement="space"
            >
              {subscription.fullPrice?.amount === 0 ||
              subscription.initialPaymentForSubscription.initialPaymentPeriod.discount.amount ===
                subscription.initialPaymentForSubscription.initialPaymentPeriod.toPay.amount ? (
                <Price
                  amount={subscription.initialPaymentForSubscription.initialPaymentPeriod.toPay.amount}
                  textDecoration="line-through"
                />
              ) : (
                <Price amount={subscription.initialPaymentForSubscription.initialPaymentPeriod.toPay.amount} />
              )}
            </SummaryItem>

            {subscription.initialPaymentForSubscription.otherPaymentPeriods.map((paymentPeriod: PaymentPeriod) => (
              <SummaryItem
                heading={capitalizeWord(dayjs(paymentPeriod.period.start).format("MMMM"))}
                key={paymentPeriod.period.start}
                horizontalPlacement="space"
              >
                {subscription.fullPrice?.amount === 0 ||
                paymentPeriod.discount.amount === paymentPeriod.toPay.amount ? (
                  <Price amount={paymentPeriod.toPay.amount} textDecoration="line-through" />
                ) : (
                  <Price amount={paymentPeriod.toPay.amount} />
                )}
              </SummaryItem>
            ))}
          </>
        ) : subscription ? (
          <SummaryItem heading={subscription.product.name} key={subscription.id} horizontalPlacement="space">
            {subscription.fullPrice?.amount === 0 ? (
              <Price amount={subscription.price.amount} textDecoration="line-through" />
            ) : (
              <Price amount={subscription.price.amount} />
            )}
          </SummaryItem>
        ) : (
          <></>
        )}

        {articles?.map((article) => (
          <SummaryItem
            heading={article.product.name}
            key={article.id + article.product.name}
            horizontalPlacement="space"
          >
            {article.price.amount === 0 ? (
              <Price amount={article.discount?.amount ?? 0} textDecoration="line-through" />
            ) : (
              <Price amount={article.price.amount} />
            )}
          </SummaryItem>
        ))}

        {valuecards?.map((valueCard) => (
          <SummaryItem
            heading={valueCard.product.name}
            key={valueCard.id + valueCard.product.name}
            horizontalPlacement="space"
          >
            {valueCard.price.amount === 0 ? (
              <Price amount={valueCard.discount?.amount ?? 0} textDecoration="line-through" />
            ) : (
              <Price amount={valueCard.price.amount} />
            )}
          </SummaryItem>
        ))}

        {order?.discount?.amount !== 0 && (
          <SummaryItem heading="Du sparer" horizontalPlacement="space" weight="bold">
            {/* TODO: sett farge på annen måte og fjern 'primary' fra TextTheme */}
            <Price amount={order?.discount?.amount ?? 0} color="primary" weight="bold" />
          </SummaryItem>
        )}
        <HorizontalSpacer size="x-small" />

        <SummaryItem heading="Totalt å betale" horizontalPlacement="space" weight="bold">
          <Price amount={order?.leftToPay.amount || 0} weight="bold" />
        </SummaryItem>

        <HorizontalSpacer size="large" />

        <TermsCheckbox
          checked={formData.summary.termsAndConditions}
          id="input-terms-and-conditions"
          onChange={(e) =>
            setFormData({
              summary: {
                termsAndConditions: e.target.checked,
                termsMarketing: formData.summary.termsMarketing,
              },
            })
          }
          label="Jeg godtar medlemsvilkårene"
          linkText="Les medlemsvilkårene her"
          name="Vilkår"
          required={true}
          url={paths.kundeservice(KundeserviceRoutes.VILKAAR_OG_REGLER_MEDLEMSVILKAAR)}
        />
        <HorizontalSpacer />

        <TermsCheckbox
          checked={formData.summary.termsMarketing ?? false}
          id="input-terms-marketing"
          onChange={(e) =>
            setFormData({
              summary: {
                termsAndConditions: formData.summary.termsAndConditions,
                termsMarketing: e.target.checked,
              },
            })
          }
          label="Jeg godtar markedsføring"
          linkText="Les mer her"
          name="Markedsføring"
          url={paths.personvernOgMarkedsforing()}
        />

        <InputErrorMessage errorMessage={validation?.error?.message} externalErrorMessage={serverSideError} />

        <HorizontalSpacer size="large" />
      </VerticalContainer>

      <HorizontalContainer horizontalPlacement="center">
        <Button
          buttonType="primary"
          loading={loading}
          onClick={async (e: SyntheticEvent) => {
            e.preventDefault();
            await handleNext();
          }}
        >
          Til betaling
        </Button>
      </HorizontalContainer>
      <HorizontalSpacer size="large" />

      {subscription?.recurringPayment && subscription?.initialPaymentForSubscription && (
        <>
          <InitialPaymentExplanation
            initialPaymentPeriodToPaymentAmount={
              subscription.initialPaymentForSubscription.initialPaymentPeriod.toPay.amount ?? 0
            }
            initialPaymentPeriodEnd={dayjs(subscription.initialPaymentForSubscription.period.end)}
            monthlyPaymentAmount={subscription.recurringPayment.price.amount}
          />
          <HorizontalSpacer />
        </>
      )}

      {formData.accountType?.disclaimer && (
        <>
          <Text size="small" align="center" value={formData.accountType.disclaimer} />
          <HorizontalSpacer />
        </>
      )}
    </PageContent>
  );
}
